import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useDiscardUnavailableData } from "@softmax/web-features/data";
import { useActualViewer } from "@softmax/web-features/session/records";
import type { PublicPlayShell } from "../lib/types";
import { boardOptions, personalOptions } from "./one-pager.queries";

export function useOnePagerData({
  shell,
  path,
}: {
  readonly shell: PublicPlayShell;
  readonly path: string;
}) {
  const viewer = useActualViewer();
  const viewerId = viewer.data?.subject_id ?? null;
  const personalQuery = personalOptions({
    leagueId: shell.league.id,
    href: path,
    viewerId,
  });
  const personal = useQuery({ ...personalQuery, enabled: viewerId !== null });
  const personalUnavailable = useDiscardUnavailableData({
    queryKey: personalQuery.queryKey,
    error: personal.error,
  });
  const [deniedPersonal, setDeniedPersonal] = useState(false);
  const personalRejected =
    personalUnavailable || (deniedPersonal && !personal.isSuccess);

  if (personalRejected !== deniedPersonal) setDeniedPersonal(personalRejected);

  const boardQuery = boardOptions({
    href: path,
    viewerId,
  });
  const boardRead = useQuery(boardQuery);
  const boardUnavailable = useDiscardUnavailableData({
    queryKey: boardQuery.queryKey,
    error: boardRead.error,
  });
  const publicBoard = {
    ...boardRead,
    data: boardUnavailable ? undefined : boardRead.data,
  };
  const personalResult = personalRejected ? undefined : personal.data;
  const data = personalResult?.data;
  const pageShell = personalResult?.shell ?? publicBoard.data?.shell ?? shell;
  const programs = data ?? (viewer.data === null ? { yours: [] } : undefined);
  const pending = viewer.isPending || personal.isFetching;
  return {
    viewer,
    viewerId,
    personal,
    publicBoard,
    data,
    pageShell,
    programs,
    pending,
  };
}
